import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Mic, Search, Clock, Loader2, Trash2, FileCheck, FilePlus2, X } from 'lucide-react'
import { api } from '../lib/api'
import { DurableAudioCapture } from '../lib/durableAudio'
import { SubscriptionBanner } from '../components/billing/SubscriptionBanner'
import ScheduleSection from '../components/schedule/ScheduleSection'
import { CoachMarks, isTourDone } from '../components/onboarding/CoachMarks'

interface Encounter {
  id: string
  patient_name: string | null
  patient_first_name?: string | null
  patient_last_name?: string | null
  encounter_type: string
  is_new_patient?: boolean | null
  capture_mode: string
  procedure_name: string | null
  encounter_date: string
  status: string
  note_count: number
  transcript: string | null
  created_at: string
  updated_at: string
  charted_at?: string | null
}

type DateFilter = 'all' | 'today' | 'yesterday' | 'this-week' | 'last-week'

const STATUS_CONFIG: Record<string, { label: string; dotColor: string; textColor: string; pulse?: boolean }> = {
  recording: { label: 'Recording', dotColor: 'bg-rose-500', textColor: 'text-rose-400', pulse: true },
  empty: { label: 'Empty', dotColor: 'bg-gray-400', textColor: 'text-gray-400' },
  processing: { label: 'Processing', dotColor: 'bg-violet-400', textColor: 'text-violet-400', pulse: true },
  draft: { label: 'Draft', dotColor: 'bg-sky-500', textColor: 'text-sky-400' },
  final: { label: 'Final', dotColor: 'bg-emerald-500', textColor: 'text-emerald-400' },
  error: { label: 'Needs attention', dotColor: 'bg-amber-500', textColor: 'text-amber-500' },
}

const TYPE_LABELS: Record<string, string> = {
  op_note: 'Op Note',
  office: 'Office Visit',
  inpatient: 'Rounding',
  consult: 'Consult',
  procedure: 'Procedure',
  telehealth: 'Telehealth',
}

const SWIPE_THRESHOLD = -100 // px to trigger delete
const SNAP_BACK_MS = 300

// Sortable column header for the Visits table. Clicking sets the sort and (via
// the parent's derived flatMode) collapses the date-grouped view into one flat
// sortable list. Same field flips asc/desc; a new field starts ascending.
function VisitSortTh({ field, label, sort, onSort, align = 'left' }: { field: string; label: string; sort: string | null; onSort: (f: string) => void; align?: 'left' | 'right' }) {
  const [activeField, dir] = (sort || '').split(':')
  const active = activeField === field
  const Up = (props: any) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
  const Down = (props: any) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
  return (
    <th className={`font-medium px-4 py-2.5 select-none cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 ${align === 'right' ? 'text-right' : 'text-left'}`} onClick={() => onSort(field)}>
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''} ${active ? 'text-brand-600 dark:text-brand-300' : ''}`}>
        {label}
        {active && (dir === 'desc' ? <Down style={{ width: 12, height: 12 }} /> : <Up style={{ width: 12, height: 12 }} />)}
      </span>
    </th>
  )
}

export function DashboardPage() {
  const navigate = useNavigate()
  const [encounters, setEncounters] = useState<Encounter[]>([])
  const [totalEncounters, setTotalEncounters] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  const PAGE_SIZE = 50
  const [loading, setLoading] = useState(true)
  const [dateFilter, setDateFilter] = useState<DateFilter>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [filingFilter, setFilingFilter] = useState<'all' | 'unfiled' | 'filed'>('all')
  // Column sort for the desktop Visits table. null = default (date-grouped) view.
  // Setting a sort OR a custom date range collapses into one flat sortable table (Option B).
  const [visitSort, setVisitSort] = useState<string | null>(null)
  const [dateFrom, setDateFrom] = useState<string>('') // YYYY-MM-DD inclusive
  const [dateTo, setDateTo] = useState<string>('')     // YYYY-MM-DD inclusive
  function toggleVisitSort(field: string) {
    const [f, d] = (visitSort || '').split(':')
    setVisitSort(f === field && d !== 'desc' ? `${field}:desc` : field)
  }
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearch, setShowSearch] = useState(false)

  // Swipe-to-delete state
  const [swipeStates, setSwipeStates] = useState<Record<string, number>>({})
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const swipeStartRef = useRef<{ x: number; y: number; id: string } | null>(null)
  const swipeLockedRef = useRef<boolean>(false)
  const latestOffsetRef = useRef<number>(0) // tracks latest offset to avoid stale closures

  // Offline dictations waiting to be synced (stored locally, started offline)
  const [pendingOffline, setPendingOffline] = useState<Array<{ encounterId: string; approxSeconds: number }>>([])
  const [syncingOffline, setSyncingOffline] = useState(false)
  const [showTour, setShowTour] = useState(false)

  // First-use coach marks: start after the page has rendered, only once ever
  useEffect(() => {
    if (isTourDone()) return
    const t = setTimeout(() => setShowTour(true), 800)
    return () => clearTimeout(t)
  }, [])

  // Handle post-checkout subscription sync
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('subscription') === 'success') {
      api.syncSubscription().catch(() => {})
      window.history.replaceState({}, '', '/')
    }
  }, [])

  useEffect(() => {
    loadEncounters()
    checkPendingOffline()
    // Re-check when connectivity returns
    const onOnline = () => checkPendingOffline()
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [])

  async function checkPendingOffline() {
    try {
      const orphans = await DurableAudioCapture.findOrphanedRecordings()
      setPendingOffline(
        orphans
          .filter((o) => o.encounterId.startsWith('offline-'))
          .map((o) => ({ encounterId: o.encounterId, approxSeconds: o.approxSeconds })),
      )
    } catch {}
  }

  async function handleSyncOffline() {
    if (!navigator.onLine) {
      alert('Still offline — connect to the internet to transcribe saved dictations.')
      return
    }
    setSyncingOffline(true)
    try {
      const token = api.getToken()
      if (!token) throw new Error('Not authenticated')
      const keyMap = JSON.parse(localStorage.getItem('scribey_offline_keys') || '{}')
      const syncedIds: string[] = []
      for (const item of pendingOffline) {
        const localKey = keyMap[item.encounterId]
        if (!localKey) continue
        // Create a real encounter, then upload the buffered audio into it
        const enc = await api.createEncounter({ encounter_type: null, capture_mode: 'ambient' })
        await DurableAudioCapture.recoverOrphanTo(item.encounterId, enc.id, token, localKey)
        delete keyMap[item.encounterId]
        localStorage.setItem('scribey_offline_keys', JSON.stringify(keyMap))
        syncedIds.push(enc.id)
      }
      await checkPendingOffline()
      await loadEncounters()
      // Take the doctor straight to the note — the note page auto-generates
      // from the recovered transcript. Without this, the synced encounter sat
      // on the dashboard with a transcript but no note (looked blank).
      if (syncedIds.length === 1) {
        navigate(`/encounter/${syncedIds[0]}/note?from=recording`)
      }
    } catch (err: any) {
      console.error('Offline sync failed:', err)
      alert(`Could not sync saved dictation: ${err.message}. Your audio is still saved — try again.`)
    } finally {
      setSyncingOffline(false)
    }
  }

  async function loadEncounters() {
    try {
      const data = await api.listEncounters(PAGE_SIZE, 0)
      setEncounters(data.encounters)
      setTotalEncounters(data.total ?? data.encounters.length)
    } catch (err) {
      console.error('Failed to load encounters:', err)
    } finally {
      setLoading(false)
    }
  }

  async function loadMoreEncounters() {
    if (loadingMore) return
    setLoadingMore(true)
    try {
      const data = await api.listEncounters(PAGE_SIZE, encounters.length)
      setEncounters((prev) => {
        const seen = new Set(prev.map((e) => e.id))
        return [...prev, ...data.encounters.filter((e: Encounter) => !seen.has(e.id))]
      })
      setTotalEncounters(data.total ?? encounters.length + data.encounters.length)
    } catch (err) {
      console.error('Failed to load more encounters:', err)
    } finally {
      setLoadingMore(false)
    }
  }

  function startOfflineCapture() {
    // No network: dictate now, transcribe later. Generate a local id + key,
    // record into the encrypted IndexedDB buffer only.
    const offlineId = `offline-${Date.now()}`
    const keyBytes = crypto.getRandomValues(new Uint8Array(32))
    let bin = ''
    keyBytes.forEach((b) => { bin += String.fromCharCode(b) })
    const keyB64 = btoa(bin)
    try {
      const map = JSON.parse(localStorage.getItem('scribey_offline_keys') || '{}')
      map[offlineId] = keyB64
      localStorage.setItem('scribey_offline_keys', JSON.stringify(map))
    } catch {
      alert('Cannot start offline capture: local storage unavailable.')
      return
    }
    navigate(`/encounter/${offlineId}?autoRecord=true`, { replace: true })
  }

  async function handleCapture() {
    // Offline-first: if the browser says we're offline, don't even try the API.
    if (!navigator.onLine) {
      startOfflineCapture()
      return
    }
    try {
      const enc = await api.createEncounter({
        encounter_type: null,
        capture_mode: 'ambient',
      })
      navigate(`/encounter/${enc.id}?autoRecord=true`, { replace: true })
    } catch (err: any) {
      console.error('Failed to create encounter:', err)
      // Network failure (flaky wifi that still reports onLine) → offline mode.
      // Server rejections (4xx like trial/cap limits) should NOT silently
      // record audio that can't be attached to an encounter later.
      const msg = String(err?.message || '')
      const isServerRejection = /trial|subscri|limit|cap|payment|past due|canceled/i.test(msg)
      if (isServerRejection) {
        alert(msg)
      } else {
        startOfflineCapture()
      }
    }
  }

  function handleNewVisit() {
    navigate('/new-encounter')
  }

  async function handleDelete(encounterId: string) {
    setDeletingId(encounterId)
    try {
      await api.deleteEncounter(encounterId)
      setEncounters((prev) => prev.filter((e) => e.id !== encounterId))
    } catch (err) {
      console.error('Failed to delete encounter:', err)
      alert('Failed to delete visit')
    } finally {
      setDeletingId(null)
      setConfirmDeleteId(null)
      latestOffsetRef.current = 0
      swipeStartRef.current = null
      swipeLockedRef.current = false
      setSwipeStates((prev) => {
        const next = { ...prev }
        delete next[encounterId]
        return next
      })
    }
  }

  // Swipe handlers
  function handleTouchStart(e: React.TouchEvent, encounterId: string) {
    swipeStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      id: encounterId,
    }
    swipeLockedRef.current = false
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (!swipeStartRef.current) return
    const dx = e.touches[0].clientX - swipeStartRef.current.x
    const dy = e.touches[0].clientY - swipeStartRef.current.y

    // Lock direction on first significant move
    if (!swipeLockedRef.current && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      swipeLockedRef.current = true
      // If more vertical than horizontal, cancel swipe
      if (Math.abs(dy) > Math.abs(dx)) {
        swipeStartRef.current = null
        return
      }
    }

    if (!swipeLockedRef.current) return

    // Only allow left swipe (negative dx)
    const clampedDx = Math.min(0, Math.max(-160, dx))
    const activeId = swipeStartRef.current?.id
    if (!activeId) return
    latestOffsetRef.current = clampedDx
    setSwipeStates((prev) => ({
      ...prev,
      [activeId]: clampedDx,
    }))
  }

  function handleTouchEnd() {
    if (!swipeStartRef.current) return
    const id = swipeStartRef.current.id
    const offset = latestOffsetRef.current

    if (offset <= SWIPE_THRESHOLD) {
      // Snapped open — show delete confirmation
      setSwipeStates((prev) => ({ ...prev, [id]: -120 }))
      setConfirmDeleteId(id)
    } else {
      // Snap back
      setSwipeStates((prev) => ({ ...prev, [id]: 0 }))
      setConfirmDeleteId(null)
    }
    swipeStartRef.current = null
  }

  // Mouse swipe for desktop
  function handleMouseDown(e: React.MouseEvent, encounterId: string) {
    swipeStartRef.current = { x: e.clientX, y: e.clientY, id: encounterId }
    swipeLockedRef.current = false

    function onMouseMove(ev: MouseEvent) {
      if (!swipeStartRef.current) return
      const dx = ev.clientX - swipeStartRef.current.x
      const dy = ev.clientY - swipeStartRef.current.y

      if (!swipeLockedRef.current && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
        swipeLockedRef.current = true
        if (Math.abs(dy) > Math.abs(dx)) {
          swipeStartRef.current = null
          document.removeEventListener('mousemove', onMouseMove)
          document.removeEventListener('mouseup', onMouseUp)
          return
        }
      }
      if (!swipeLockedRef.current) return

      const clampedDx = Math.min(0, Math.max(-160, dx))
      const activeId = swipeStartRef.current?.id
      if (!activeId) return
      latestOffsetRef.current = clampedDx
      setSwipeStates((prev) => ({
        ...prev,
        [activeId]: clampedDx,
      }))
    }

    function onMouseUp() {
      if (swipeStartRef.current) {
        const sid = swipeStartRef.current.id
        const offset = latestOffsetRef.current
        if (offset <= SWIPE_THRESHOLD) {
          setSwipeStates((prev) => ({ ...prev, [sid]: -120 }))
          setConfirmDeleteId(sid)
        } else {
          setSwipeStates((prev) => ({ ...prev, [sid]: 0 }))
        }
        swipeStartRef.current = null
      }
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  // Day-bucket from a UTC instant rendered in the BROWSER's local timezone.
  // We use created_at (a real UTC timestamp) and let the browser localize it,
  // so "today" means today wherever the viewer is. Returns local midnight of
  // the instant's local calendar day.
  function localDay(value: string): Date {
    const d = new Date(String(value || ''))
    return isNaN(d.getTime()) ? d : new Date(d.getFullYear(), d.getMonth(), d.getDate())
  }

  function getDateGroup(value: string): string {
    const encounterDate = localDay(value)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const weekAgo = new Date(today)
    weekAgo.setDate(weekAgo.getDate() - 7)

    if (encounterDate >= today) return 'Today'
    if (encounterDate >= yesterday) return 'Yesterday'
    if (encounterDate >= weekAgo) return 'This Week'
    return 'Earlier'
  }

  function isInDateRange(encounter: Encounter, filter: DateFilter): boolean {
    if (filter === 'all') return true

    const encounterDate = localDay(encounter.created_at)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const weekAgo = new Date(today)
    weekAgo.setDate(weekAgo.getDate() - 7)
    const twoWeeksAgo = new Date(today)
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14)

    switch (filter) {
      case 'today':
        return encounterDate >= today
      case 'yesterday':
        return encounterDate >= yesterday && encounterDate < today
      case 'this-week':
        return encounterDate >= weekAgo
      case 'last-week':
        return encounterDate >= twoWeeksAgo && encounterDate < weekAgo
      default:
        return true
    }
  }

  function matchesSearch(encounter: Encounter, query: string): boolean {
    if (!query.trim()) return true
    const q = query.toLowerCase()
    return !!(
      encounter.patient_name?.toLowerCase().includes(q) ||
      encounter.procedure_name?.toLowerCase().includes(q) ||
      TYPE_LABELS[encounter.encounter_type]?.toLowerCase().includes(q) ||
      encounter.status.toLowerCase().includes(q)
    )
  }

  // A custom from/to range is active when either bound is set; it overrides the preset dateFilter.
  const hasCustomRange = !!(dateFrom || dateTo)
  function inCustomRange(enc: Encounter): boolean {
    if (!hasCustomRange) return true
    // Compare on the encounter's LOCAL calendar day (from the UTC created_at).
    const ld = localDay(enc.created_at)
    if (isNaN(ld.getTime())) return false
    const d = `${ld.getFullYear()}-${String(ld.getMonth() + 1).padStart(2, '0')}-${String(ld.getDate()).padStart(2, '0')}`
    if (dateFrom && d < dateFrom) return false
    if (dateTo && d > dateTo) return false
    return true
  }

  const filteredEncounters = encounters
    .filter((enc) => hasCustomRange ? inCustomRange(enc) : isInDateRange(enc, dateFilter))
    .filter((enc) => typeFilter === 'all' || enc.encounter_type === typeFilter)
    .filter((enc) => {
      if (filingFilter === 'all') return true
      const hasNote = enc.note_count > 0
      if (filingFilter === 'filed') return !!enc.charted_at
      // 'unfiled' = has a note but not yet entered into EHR (the work-to-do list)
      return hasNote && !enc.charted_at
    })
    .filter((enc) => matchesSearch(enc, searchQuery))

  // Comparator for column sorting (flat mode only).
  function encTimestamp(enc: Encounter): number {
    const t = new Date(enc.created_at).getTime()
    return isNaN(t) ? 0 : t
  }
  function sortEncounters(list: Encounter[], sortKey: string): Encounter[] {
    const [field, dir] = sortKey.split(':')
    const mul = dir === 'desc' ? -1 : 1
    const arr = [...list]
    arr.sort((a, b) => {
      let c = 0
      // Fallbacks: derive last/first from "Last, First" patient_name when explicit fields absent.
      const lastOf = (e: Encounter) => e.patient_last_name || (e.patient_name || '').split(',')[0] || ''
      const firstOf = (e: Encounter) => e.patient_first_name || (e.patient_name || '').split(',')[1]?.trim() || ''
      switch (field) {
        case 'patient': c = (a.patient_name || '').localeCompare(b.patient_name || '', undefined, { sensitivity: 'base' }); break
        case 'last_name': c = lastOf(a).localeCompare(lastOf(b), undefined, { sensitivity: 'base' }); break
        case 'first_name': c = firstOf(a).localeCompare(firstOf(b), undefined, { sensitivity: 'base' }); break
        case 'type': c = (TYPE_LABELS[a.encounter_type] || a.encounter_type || '').localeCompare(TYPE_LABELS[b.encounter_type] || b.encounter_type || ''); break
        case 'status': c = (a.status || '').localeCompare(b.status || ''); break
        case 'ehr': c = (a.charted_at ? 1 : 0) - (b.charted_at ? 1 : 0); break
        case 'time': c = encTimestamp(a) - encTimestamp(b); break
        default: c = encTimestamp(a) - encTimestamp(b)
      }
      if (c === 0) c = encTimestamp(a) - encTimestamp(b) // stable-ish tiebreak by time
      return c * mul
    })
    return arr
  }

  // Option B: flat sortable table when a column sort OR a custom date range is active;
  // otherwise keep the familiar date-grouped view. displayGroups unifies both paths so
  // the table/card JSX below renders identically for each "group".
  const flatMode = !!visitSort || hasCustomRange
  let displayGroups: { name: string | null; items: Encounter[] }[]
  if (flatMode) {
    const items = visitSort ? sortEncounters(filteredEncounters, visitSort) : filteredEncounters
    displayGroups = [{ name: null, items }]
  } else {
    const grouped: Record<string, Encounter[]> = {}
    filteredEncounters.forEach((enc) => {
      const group = getDateGroup(enc.created_at)
      if (!grouped[group]) grouped[group] = []
      grouped[group].push(enc)
    })
    const groupOrder = ['Today', 'Yesterday', 'This Week', 'Earlier']
    displayGroups = groupOrder.filter((g) => grouped[g]).map((g) => ({ name: g, items: grouped[g] }))
  }

  function formatTime(enc: Encounter): string {
    // Wall-clock time from the UTC instant, localized to the browser timezone.
    const date = new Date(enc.created_at)
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  }

  function getDuration(encounter: Encounter): string {
    const created = new Date(encounter.created_at)
    const updated = new Date(encounter.updated_at)
    const diffMs = updated.getTime() - created.getTime()
    const mins = Math.floor(diffMs / 60000)
    // updated_at moves on note regen/edits — beyond a plausible visit length
    // the delta is editing history, not visit duration. Hide it.
    if (mins > 120) return ''
    if (mins < 1) return '<1 min'
    if (mins < 60) return `${mins} min`
    const hours = Math.floor(mins / 60)
    const remainingMins = mins % 60
    return `${hours}h ${remainingMins}m`
  }

  return (
    <div className="max-w-5xl mx-auto pb-44 md:pb-24">
      {showTour && <CoachMarks onDone={() => setShowTour(false)} />}

      {/* Pending offline dictations banner */}
      {pendingOffline.length > 0 && (
        <div className="mb-4 flex items-center justify-between gap-3 bg-amber-50 dark:bg-amber-900/15 border border-amber-300 dark:border-amber-700 rounded-lg px-4 py-3">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            {pendingOffline.length === 1
              ? `1 dictation recorded offline (~${pendingOffline[0].approxSeconds}s) is waiting to be transcribed.`
              : `${pendingOffline.length} dictations recorded offline are waiting to be transcribed.`}
          </p>
          <button
            onClick={handleSyncOffline}
            disabled={syncingOffline}
            className="text-sm px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 flex-shrink-0"
          >
            {syncingOffline ? 'Transcribing…' : 'Transcribe now'}
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Visits</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Search visits"
          >
            <Search size={20} />
          </button>
          <button
            data-tour="capture"
            onClick={handleCapture}
            className="hidden sm:flex items-center gap-2 bg-brand-600 hover:bg-brand-500 active:bg-brand-800 text-white px-4 py-2 rounded-xl font-semibold shadow-sm transition-colors"
          >
            <Mic size={18} />
            Capture
          </button>
        </div>
      </div>

      <SubscriptionBanner />

      {/* Schedule Section */}
      <ScheduleSection />

      {/* Search bar */}
      {showSearch && (
        <div className="mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by patient, procedure, or status..."
            className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-2.5 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            autoFocus
          />
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-2">
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value as DateFilter)}
          disabled={hasCustomRange}
          title={hasCustomRange ? 'Clear the date range to use presets' : undefined}
          className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-40"
        >
          <option value="all">All dates</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="this-week">This week</option>
          <option value="last-week">Last week</option>
        </select>
        {/* Custom date range (from/to). Native date inputs — lightweight, accessible, no deps.
            Setting either bound overrides the preset filter and switches Visits into flat sortable mode. */}
        <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-2 py-1">
          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            aria-label="From date"
            className="bg-transparent text-gray-800 dark:text-gray-200 text-sm px-1 py-1 focus:outline-none"
          />
          <span className="text-gray-400 dark:text-gray-500 text-xs">to</span>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            aria-label="To date"
            className="bg-transparent text-gray-800 dark:text-gray-200 text-sm px-1 py-1 focus:outline-none"
          />
          {hasCustomRange && (
            <button
              onClick={() => { setDateFrom(''); setDateTo('') }}
              title="Clear date range"
              className="p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="all">All types</option>
          <option value="office">Office Visit</option>
          <option value="op_note">Op Note</option>
          <option value="consult">Consult</option>
          <option value="inpatient">Rounding</option>
          <option value="procedure">Procedure</option>
          <option value="telehealth">Telehealth</option>
        </select>
        <select
          value={filingFilter}
          onChange={(e) => setFilingFilter(e.target.value as 'all' | 'unfiled' | 'filed')}
          className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          title="Filter by whether the note has been entered into your EHR"
        >
          <option value="all">All notes</option>
          <option value="unfiled">Needs filing</option>
          <option value="filed">Entered in EHR</option>
        </select>
        <button
          data-tour="new-visit"
          onClick={handleNewVisit}
          className="md:hidden flex items-center gap-1.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg text-sm font-medium transition-colors ml-auto"
        >
          <Plus size={14} />
          New
        </button>
      </div>

      {/* Visit List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-gray-100 dark:bg-gray-900 rounded-lg h-20 animate-pulse border border-gray-200 dark:border-gray-800" />
          ))}
        </div>
      ) : filteredEncounters.length === 0 ? (
        <div className="py-8 sm:py-14 text-center">
          <div className="w-14 h-14 rounded-2xl bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center mx-auto mb-4">
            <Mic size={26} className="text-brand-500" />
          </div>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1.5">
            {encounters.length === 0 ? 'No visits yet' : 'No visits found'}
          </h2>
          <p className="text-gray-400 dark:text-gray-500 mb-6 max-w-sm mx-auto">
            {encounters.length === 0
              ? 'Hit Capture and start talking — your note writes itself'
              : 'Try adjusting your search or filter'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {flatMode && (
            <p className="hidden md:block text-xs text-gray-400 dark:text-gray-500 -mb-2">
              {hasCustomRange ? 'Showing visits in selected date range' : 'Sorted view'}{visitSort ? ' — click a column to re-sort, or clear sort' : ''}
              {visitSort && (
                <button onClick={() => setVisitSort(null)} className="ml-2 underline hover:text-gray-600 dark:hover:text-gray-300">clear sort</button>
              )}
            </p>
          )}
          {displayGroups.map((grp) => (
            <div key={grp.name || '__flat__'}>
              {grp.name && (
                <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                  {grp.name}
                </h3>
              )}

              {/* Desktop table view */}
              <div className="hidden md:block bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-gray-200/60 dark:bg-gray-900/60 text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide border-b border-gray-200 dark:border-gray-800">
                    <tr>
                      <VisitSortTh field="last_name" label="Last name" sort={visitSort} onSort={toggleVisitSort} />
                      <VisitSortTh field="first_name" label="First name" sort={visitSort} onSort={toggleVisitSort} />
                      <VisitSortTh field="type" label="Type" sort={visitSort} onSort={toggleVisitSort} />
                      <VisitSortTh field="time" label="Time" sort={visitSort} onSort={toggleVisitSort} />
                      <VisitSortTh field="status" label="Status" sort={visitSort} onSort={toggleVisitSort} />
                      <VisitSortTh field="ehr" label="EHR" sort={visitSort} onSort={toggleVisitSort} />
                      <th className="px-4 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {grp.items.map((enc) => {
                      const sc = STATUS_CONFIG[enc.status] || { label: enc.status, dotColor: 'bg-gray-500', textColor: 'text-gray-500' }
                      return (
                        <tr
                          key={enc.id}
                          onClick={() => {
                            if (enc.note_count > 0 || (enc.transcript && enc.transcript.trim())) navigate(`/encounter/${enc.id}/note`)
                            else navigate(`/encounter/${enc.id}`)
                          }}
                          className="hover:bg-gray-200/60 dark:hover:bg-gray-800/50 cursor-pointer group"
                        >
                          <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                            {(enc.patient_last_name || (enc.patient_name || '').split(',')[0] || '—') || '—'}
                            {enc.is_new_patient && <span className="ml-2 text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded uppercase tracking-wide">New</span>}
                          </td>
                          <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{enc.patient_first_name || (enc.patient_name || '').split(',')[1]?.trim() || '—'}</td>
                          <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">{TYPE_LABELS[enc.encounter_type] || enc.encounter_type || '—'}</td>
                          <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">{formatTime(enc)}</td>
                          <td className="px-4 py-2.5">
                            {enc.status !== 'empty' ? (
                              <span className="inline-flex items-center gap-1.5">
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${sc.dotColor} ${sc.pulse ? 'animate-pulse' : ''}`} />
                                <span className={`text-xs font-medium ${sc.textColor}`}>{sc.label}</span>
                              </span>
                            ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                          </td>
                          <td className="px-4 py-2.5">
                            {enc.note_count > 0 ? (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation()
                                  const next = !enc.charted_at
                                  setEncounters((prev) => prev.map((x) => x.id === enc.id ? { ...x, charted_at: next ? new Date().toISOString() : null } : x))
                                  try { await api.setCharted(enc.id, next) } catch { setEncounters((prev) => prev.map((x) => x.id === enc.id ? { ...x, charted_at: enc.charted_at } : x)) }
                                }}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${enc.charted_at ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400' : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200/60 dark:hover:bg-gray-800'}`}
                                title={enc.charted_at ? 'Entered in EHR — click to unmark' : 'Mark as entered into your EHR'}
                              >
                                {enc.charted_at ? <FileCheck size={13} /> : <FilePlus2 size={13} />}
                                {enc.charted_at ? 'In EHR' : 'File'}
                              </button>
                            ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <button
                              onClick={(e) => { e.stopPropagation(); if (confirm('Delete this visit?')) handleDelete(enc.id) }}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete visit"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile card view */}
              <div className="md:hidden space-y-2">
                {grp.items.map((enc) => {
                  const statusConfig = STATUS_CONFIG[enc.status] || {
                    label: enc.status,
                    dotColor: 'bg-gray-500',
                    textColor: 'text-gray-500',
                  }
                  const swipeOffset = swipeStates[enc.id] || 0
                  const isDeleting = deletingId === enc.id
                  const showDeleteBtn = confirmDeleteId === enc.id

                  return (
                    <div key={enc.id} className="relative overflow-hidden rounded-lg">
                      {/* Delete background (revealed on swipe) */}
                      <div className="absolute inset-0 bg-red-600 flex items-center justify-end px-6 rounded-lg">
                        {showDeleteBtn ? (
                          <button
                            onClick={() => handleDelete(enc.id)}
                            disabled={isDeleting}
                            className="flex items-center gap-2 text-white font-medium"
                          >
                            {isDeleting ? (
                              <Loader2 size={20} className="animate-spin" />
                            ) : (
                              <Trash2 size={20} />
                            )}
                            {isDeleting ? 'Deleting...' : 'Delete'}
                          </button>
                        ) : (
                          <Trash2 size={20} className="text-white/70" />
                        )}
                      </div>

                      {/* Swipeable card */}
                      <div
                        onTouchStart={(e) => handleTouchStart(e, enc.id)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onMouseDown={(e) => handleMouseDown(e, enc.id)}
                        onClick={() => {
                          // Only navigate if not swiped
                          if (Math.abs(swipeOffset) < 10 && !showDeleteBtn) {
                            if (enc.note_count > 0 || (enc.transcript && enc.transcript.trim())) {
                              // Has a note — or a transcript awaiting note
                              // generation (e.g. recovered offline audio).
                              navigate(`/encounter/${enc.id}/note`)
                            } else {
                              navigate(`/encounter/${enc.id}`)
                            }
                          }
                          // If showing delete, tap card to dismiss
                          if (showDeleteBtn) {
                            setSwipeStates((prev) => ({ ...prev, [enc.id]: 0 }))
                            setConfirmDeleteId(null)
                          }
                        }}
                        className="relative bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4 text-left group select-none"
                        style={{
                          transform: `translateX(${swipeOffset}px)`,
                          transition: swipeStartRef.current?.id === enc.id ? 'none' : `transform ${SNAP_BACK_MS}ms ease-out`,
                        }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                {enc.patient_name || 'Unknown Patient'}
                              </h3>
                              {enc.encounter_type && (
                                <span className="text-xs bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded flex-shrink-0">
                                  {TYPE_LABELS[enc.encounter_type] || enc.encounter_type}
                                </span>
                              )}
                              {enc.is_new_patient && (
                                <span className="text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded flex-shrink-0 uppercase tracking-wide">
                                  New
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-400 dark:text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {formatTime(enc)}
                              </span>
                              {getDuration(enc) && (
                                <>
                                  <span>•</span>
                                  <span>{getDuration(enc)}</span>
                                </>
                              )}
                              {enc.procedure_name && (
                                <>
                                  <span>•</span>
                                  <span className="truncate">{enc.procedure_name}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Desktop delete button (hover) */}
                            <button
                              onClick={(e) => { e.stopPropagation(); if (confirm('Delete this visit?')) handleDelete(enc.id) }}
                              className="hidden md:group-hover:flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                              title="Delete visit"
                            >
                              <Trash2 size={14} />
                            </button>
                            {/* Entered-in-EHR toggle — only when a note exists to file */}
                            {enc.note_count > 0 && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation()
                                  const next = !enc.charted_at
                                  // optimistic
                                  setEncounters((prev) => prev.map((x) => x.id === enc.id ? { ...x, charted_at: next ? new Date().toISOString() : null } : x))
                                  try { await api.setCharted(enc.id, next) }
                                  catch { setEncounters((prev) => prev.map((x) => x.id === enc.id ? { ...x, charted_at: enc.charted_at } : x)) }
                                }}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${enc.charted_at
                                  ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400'
                                  : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200/60 dark:hover:bg-gray-800'}`}
                                title={enc.charted_at ? 'Entered in EHR — click to unmark' : 'Mark as entered into your EHR'}
                              >
                                {enc.charted_at ? <FileCheck size={13} /> : <FilePlus2 size={13} />}
                                <span className="hidden sm:inline">{enc.charted_at ? 'In EHR' : 'File'}</span>
                              </button>
                            )}
                            {enc.status !== 'empty' && (
                              <div className="flex items-center gap-1.5">
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${statusConfig.dotColor} ${statusConfig.pulse ? 'animate-pulse' : ''}`} />
                                <span className={`text-xs font-medium ${statusConfig.textColor}`}>
                                  {statusConfig.label}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

          {/* Load more — removes the 50-visit ceiling. Hidden while filtering a subset. */}
          {encounters.length < totalEncounters && (
            <div className="flex flex-col items-center gap-1 pt-2 pb-4">
              <button
                onClick={loadMoreEncounters}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50"
              >
                {loadingMore ? <Loader2 size={15} className="animate-spin" /> : null}
                {loadingMore ? 'Loading…' : 'Load more visits'}
              </button>
              <span className="text-[11px] text-gray-400 dark:text-gray-500">Showing {encounters.length} of {totalEncounters}</span>
            </div>
          )}
        </div>
      )}

      {/* Capture Button — mobile only (desktop has it in top bar) */}
      <div className="md:hidden fixed bottom-20 left-1/2 -translate-x-1/2 z-50">
        <button
          data-tour="capture"
          onClick={handleCapture}
          className="flex items-center gap-3 bg-brand-600 hover:bg-brand-500 text-white px-8 py-4 rounded-full font-bold text-lg shadow-2xl transition-all hover:scale-105 active:scale-95"
        >
          <Mic size={24} />
          Capture
        </button>
      </div>
    </div>
  )
}
